import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const workWithMeLinks = [
  { to: "/private-sessions", label: "Private Sessions" },
  { to: "/the-menopause-invitation-live", label: "The Menopause Invitation — Live (Aug)" },
  { to: "/the-rewired-life", label: "The Rewired Life" },
  { to: "/the-menopause-invitation", label: "The Menopause Invitation" },
  { to: "/a-mans-guide-to-her-menopause", label: "A Man's Guide to Her Menopause" },
  { to: "/free-resources", label: "Free Resources" },
];

const topLevelLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { type: "dropdown", label: "Work With Me", children: workWithMeLinks },
  { to: "/a-mans-guide-to-her-menopause", label: "For Men" },
  { to: "/insights", label: "The Journal" },
  { to: "/podcast", label: "Podcast" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [workDropdownOpen, setWorkDropdownOpen] = useState(false);
  const [mobileWorkOpen, setMobileWorkOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close dropdown when route changes
  useEffect(() => {
    setWorkDropdownOpen(false);
    setOpen(false);
    setMobileWorkOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const goToNewsletter = (e) => {
    e.preventDefault();
    setOpen(false);
    if (window.location.pathname === "/") {
      document.getElementById("newsletter")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/");
      setTimeout(() => {
        document.getElementById("newsletter")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  const isWorkActive = workWithMeLinks.some((l) => location.pathname === l.to);

  return (
    <header
      data-testid="site-nav"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-cream/85 backdrop-blur-xl border-b border-hairline/60" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <span className="font-serif-display text-2xl md:text-[1.7rem] text-ink leading-none">
            Pip<span className="text-gold italic"> Bennett</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {topLevelLinks.map((l) => {
            if (l.type === "dropdown") {
              return (
                <div
                  key={l.label}
                  className="relative"
                  onMouseEnter={() => setWorkDropdownOpen(true)}
                  onMouseLeave={() => setWorkDropdownOpen(false)}
                  data-testid="nav-work-with-me"
                >
                  <button
                    type="button"
                    onClick={() => setWorkDropdownOpen((v) => !v)}
                    aria-expanded={workDropdownOpen}
                    aria-haspopup="true"
                    className={`inline-flex items-center gap-1 text-[0.82rem] tracking-wide font-medium transition-colors ${
                      isWorkActive ? "text-sage" : "text-ink hover:text-sage"
                    }`}
                  >
                    {l.label}
                    <ChevronDown
                      size={14}
                      strokeWidth={1.8}
                      className={`transition-transform ${workDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {workDropdownOpen && (
                    <div
                      className="absolute left-0 top-full pt-3 w-72"
                      data-testid="nav-work-dropdown"
                    >
                      <div className="bg-cream border border-hairline/60 rounded-2xl shadow-[0_20px_50px_-20px_rgba(74,93,79,0.25)] py-3 overflow-hidden">
                        {l.children.map((child) => (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            onClick={() => setWorkDropdownOpen(false)}
                            data-testid={`nav-work-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                              `block px-5 py-2.5 text-sm transition-colors ${
                                isActive ? "text-sage bg-sage/5" : "text-ink hover:text-sage hover:bg-sage/5"
                              }`
                            }
                          >
                            {child.label}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            if (l.isAnchor) {
              return (
                <a
                  key={l.to}
                  href={l.to}
                  onClick={goToNewsletter}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-[0.82rem] tracking-wide font-medium text-ink hover:text-sage transition-colors"
                >
                  {l.label}
                </a>
              );
            }
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[0.82rem] tracking-wide font-medium transition-colors ${
                    isActive ? "text-sage" : "text-ink hover:text-sage"
                  }`
                }
              >
                {l.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Right-side: only show Dashboard/Sign-out for logged-in users (admin etc) */}
        <div className="hidden lg:flex items-center gap-4">
          {user && user !== false && (
            <>
              <Link
                to="/dashboard"
                data-testid="nav-dashboard-btn"
                className="text-[0.82rem] font-medium text-sage hover:text-sage-dark transition"
              >
                Dashboard
              </Link>
              <button
                onClick={handleLogout}
                data-testid="nav-logout-btn"
                className="text-[0.82rem] font-medium text-ink-soft hover:text-ink transition"
              >
                Sign out
              </button>
            </>
          )}
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 text-ink"
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-cream border-t border-hairline/60">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
            {topLevelLinks.map((l) => {
              if (l.type === "dropdown") {
                return (
                  <div key={l.label}>
                    <button
                      type="button"
                      onClick={() => setMobileWorkOpen((v) => !v)}
                      className="w-full flex items-center justify-between text-ink text-lg font-serif-display"
                      data-testid="nav-mobile-work-with-me"
                    >
                      <span>{l.label}</span>
                      <ChevronDown
                        size={18}
                        strokeWidth={1.5}
                        className={`transition-transform ${mobileWorkOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {mobileWorkOpen && (
                      <div className="mt-3 ml-3 pl-3 border-l border-hairline/60 flex flex-col gap-3">
                        {l.children.map((child) => (
                          <Link
                            key={child.to}
                            to={child.to}
                            onClick={() => setOpen(false)}
                            data-testid={`nav-mobile-work-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="text-ink-soft text-base"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              if (l.isAnchor) {
                return (
                  <a
                    key={l.to}
                    href={l.to}
                    onClick={goToNewsletter}
                    data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-ink text-lg font-serif-display"
                  >
                    {l.label}
                  </a>
                );
              }
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-ink text-lg font-serif-display"
                >
                  {l.label}
                </Link>
              );
            })}
            {user && user !== false && (
              <div className="pt-3 border-t border-hairline/60 flex flex-col gap-4">
                <Link
                  to="/dashboard"
                  onClick={() => setOpen(false)}
                  className="text-sage font-medium"
                  data-testid="nav-mobile-dashboard"
                >
                  Dashboard
                </Link>
                <button onClick={handleLogout} className="text-ink-soft text-left" data-testid="nav-mobile-logout">
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
